import {
  Background,
  Connection,
  Edge,
  EdgeTypes,
  FitViewOptions,
  Node,
  NodeChange,
  NodeTypes,
  ReactFlow,
} from '@xyflow/react';
import React, { MouseEvent as ReactMouseEvent, ReactNode } from 'react';

interface QueryVisualizerCanvasProps {
  rfNodes: Node[];
  rfEdges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  nodeTypes: NodeTypes;
  edgeTypes: EdgeTypes;
  editable: boolean;
  isPlacementMode: boolean;
  onCanvasClick: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onConnect: (connection: Connection) => void;
  fitViewOptions: FitViewOptions<Node>;
  ghostIconUrl: string | null;
  cursorPosition: { x: number; y: number } | null;
  children?: ReactNode;
}

/**
 * QueryVisualizerCanvas - ReactFlow canvas wrapper
 *
 * Renders:
 * - Main ReactFlow canvas with nodes and edges
 * - Instructional banner for placement mode
 * - SVG arrow markers for edges
 * - Background grid
 * - Ghost node that follows cursor in placement mode
 * - Toolbar controls (via children)
 *
 * Handles:
 * - Node dragging and positioning
 * - Edge connections
 * - Placement mode interactions
 */
export const QueryVisualizerCanvas = ({
  rfNodes,
  rfEdges,
  onNodesChange,
  nodeTypes,
  edgeTypes,
  editable,
  isPlacementMode,
  onCanvasClick,
  onConnect,
  fitViewOptions,
  ghostIconUrl,
  cursorPosition,
  children,
}: QueryVisualizerCanvasProps) => {
  return (
    <div
      className="bg-chariot-background-secondary relative flex-1"
      onClick={onCanvasClick}
    >
      {/* Instructional banner - shown during placement mode */}
      {isPlacementMode && (
        <div
          role="status"
          aria-live="polite"
          className="bg-chariot-background-primary/90 border-chariot-stroke text-chariot-text-primary absolute top-4 left-1/2 z-20 -translate-x-1/2 rounded-lg border px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-xs"
          data-testid="placement-instruction-banner"
        >
          Click anywhere to place node •{' '}
          <span className="text-chariot-text-secondary">ESC to cancel</span>
        </div>
      )}

      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={editable && !isPlacementMode}
        nodesConnectable={editable && !isPlacementMode}
        onConnect={onConnect}
        fitViewOptions={fitViewOptions}
        panOnScroll
        panOnDrag={!isPlacementMode}
        minZoom={0.1}
        maxZoom={4}
        zoomOnDoubleClick={false}
        defaultEdgeOptions={{
          type: 'relationship',
        }}
        className={isPlacementMode ? 'cursor-crosshair' : ''}
      >
        {/* Arrow markers for directed edges */}
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            {/* Default arrow marker */}
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth={6}
              markerHeight={6}
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-chariot-stroke" />
            </marker>
            {/* Hover arrow marker */}
            <marker
              id="arrow-hover"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth={6}
              markerHeight={6}
              orient="auto-start-reverse"
            >
              <path
                d="M 0 0 L 10 5 L 0 10 z"
                fill="rgb(var(--chariot-brand-primary))"
              />
            </marker>
          </defs>
        </svg>

        {children}
        <Background color="rgb(var(--chariot-stroke) / 0.3)" size={4} />

        {/* Ghost node - follows cursor during placement mode */}
        {isPlacementMode && cursorPosition && (
          <div
            data-testid="ghost-node"
            className="bg-chariot-background-primary flex size-[72px] items-center justify-center rounded-full border-4"
            style={{
              opacity: 0.4,
              borderColor: 'rgb(var(--chariot-brand-primary))',
              pointerEvents: 'none',
              position: 'fixed',
              transform: 'translate(-50%, -50%)',
              left: `${cursorPosition.x}px`,
              top: `${cursorPosition.y}px`,
              zIndex: 9999,
            }}
          >
            {ghostIconUrl && (
              <img
                src={ghostIconUrl}
                alt="Ghost node"
                className="size-[56px] rounded-full"
              />
            )}
          </div>
        )}
      </ReactFlow>
    </div>
  );
};
